import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  canEditBuoni: () => boolean;
  canDeleteBuoni: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      try {
        if (token && savedUser) {
          setUser(JSON.parse(savedUser));
          // Verifica che il token sia ancora valido (timeout 10s in api)
          const currentUser = await authService.me();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string, remember: boolean = false) => {
    const response = await authService.login(username, password, remember);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    if (remember && response.expires_at) {
      localStorage.setItem('token_expires_at', response.expires_at);
    }
    setUser(response.user);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Errore durante il logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const canEditBuoni = () => {
    if (isAdmin()) return true;
    return !!user?.can_edit_buoni;
  };

  const canDeleteBuoni = () => {
    if (isAdmin()) return true;
    return !!user?.can_delete_buoni;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, canEditBuoni, canDeleteBuoni }}>
      {children}
    </AuthContext.Provider>
  );
};
